import type { ReactNode } from 'react'
import { Badge, Group, Stack, Table, Text } from '@mantine/core'
import { MetaList, SectionCard } from '@lighthouse/design'
import { CartographerLink } from '../cartographer/CartographerLink'
import { EpisodeStateCell } from '../retirement/EpisodeStateCell'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { useReportDetailGovernance } from '../../hooks/useReportDetail'
import { useEpisodeStates, useRetirementEpisodes } from '../../hooks/useRetirementEpisodes'
import { adminPageMayFetch } from '../../utils/adminGate'
import { NOT_MEASURED, exactNumber, whenLabel } from '../../utils/format'
import { episodeStatusText } from '../../types/retirementEpisodes'
import type { RetirementEpisodes } from '../../types/retirementEpisodes'
import type { ReportDetailGovernance, ReportDetailResponsibilityChange } from '../../types/reportDetail'
import { Caption, EvaluatedAt, Figure, TabFailure, TabLoading } from './OverviewTab'
import {
  EPISODES_MIGRATION,
  RESPONSIBILITY_HISTORY_POINTER,
  ownerText,
  principalText,
  retirementSignalLabel,
  retirementSummary,
  retirementThresholdText,
  showingText,
} from './reportDetailFormat'

/** The most episodes the panel asks for: the server's largest page. */
const EPISODE_PAGE_SIZE = 50

/**
 * Report Detail → Governance: ownership and its history, the attention flag as
 * stored, the archive stamp, the retirement signals with their thresholds, and
 * this report's retirement episodes.
 *
 * Two reads that FAIL APART. The governance read needs no pending migration; the
 * episodes panel reads ReportBuilder.RetirementCandidate, which migration
 * AddRetirementCandidate creates and which is applied nowhere yet — so the
 * panel's 500 is said beside a governance read that rendered.
 *
 * Read-only (D6a): nothing here reassigns, keeps, delists or reverses. Each of
 * those is Cartographer's, and the hand-off is a link to it.
 */
export function GovernanceTab({ reportId }: { reportId: string }) {
  const { data, isLoading, isError, error } = useReportDetailGovernance(reportId, { open: true })

  return (
    <Stack gap="lg">
      {isError ? (
        <TabFailure error={error} what="this report’s governance" />
      ) : isLoading || !data ? (
        <TabLoading />
      ) : (
        <GovernanceBody governance={data} />
      )}

      <EpisodesPanel reportId={reportId} />
    </Stack>
  )
}

function GovernanceBody({ governance }: { governance: ReportDetailGovernance }) {
  const { attention, ownership, archive, retirement, certification } = governance

  return (
    <Stack gap="lg">
      <SectionCard
        title="Attention"
        subtitle="As stored: the flag is the status, and its time is when the orphan check detected the gap"
        action={<CartographerLink path="/governance" label="Governance">Cartographer’s Governance page</CartographerLink>}
      >
        <MetaList
          items={[
            { label: 'Flagged', value: attention.isFlagged ? 'Yes — AttentionRequired' : 'No' },
            { label: 'Since', value: whenLabel(attention.since) },
            { label: 'Reason', value: attention.reason ?? NOT_MEASURED },
          ]}
        />
      </SectionCard>

      <SectionCard title="Ownership" subtitle="The owner reading, and the columns it was read from">
        <MetaList
          items={[
            { label: 'Owner', value: ownerText(ownership.owner) },
            {
              label: 'Business owner (stored)',
              value: principalText(ownership.businessOwnerPrincipalId, ownership.businessOwnerPrincipalType),
            },
            {
              label: 'Maintainer (stored)',
              value: principalText(ownership.maintainerPrincipalId, ownership.maintainerPrincipalType),
            },
            { label: 'Author', value: ownership.authorUserId ?? NOT_MEASURED, mono: true },
            { label: 'Creator (stamped at creation)', value: ownership.creatorSnapshot ?? `${NOT_MEASURED} not stamped` },
          ]}
        />
      </SectionCard>

      <SectionCard
        title="Responsibility history"
        subtitle={`${showingText(governance.responsibilityHistory.length, governance.responsibilityHistoryCount)} transfers, newest first`}
      >
        <Stack gap="sm">
          <Caption>{governance.responsibilityHistoryCaption} {RESPONSIBILITY_HISTORY_POINTER}</Caption>
          {governance.responsibilityHistory.length === 0 ? (
            <Text size="sm" c="dimmed">No business-owner or maintainer transfer is recorded for this report.</Text>
          ) : (
            <ResponsibilityTable rows={governance.responsibilityHistory} />
          )}
        </Stack>
      </SectionCard>

      <SectionCard title="Archive" subtitle="The archive stamp, as stored">
        <MetaList
          items={[
            { label: 'Archived', value: whenLabel(archive.archivedAt) },
            { label: 'By', value: archive.archivedBy ?? NOT_MEASURED, mono: archive.archivedBy != null },
            { label: 'Reason', value: archive.archiveReason ?? NOT_MEASURED },
          ]}
        />
      </SectionCard>

      <SectionCard title="Retirement signals" subtitle="Computed exactly as the candidate list computes them">
        <Stack gap="sm">
          <Text size="sm" data-figure="retirement-signals">{retirementSummary(retirement)}</Text>
          <Caption>{retirementThresholdText(retirement)}</Caption>
          <MetaList
            items={[
              {
                label: 'Days since the last recorded run',
                value: retirement.daysSinceLastRun == null
                  ? <Figure value={NOT_MEASURED} reason="no run has been recorded" />
                  : exactNumber(retirement.daysSinceLastRun),
              },
              { label: 'Run log reaches back to', value: whenLabel(retirement.runLogReachesBackTo) },
              {
                label: 'Covers the unread window',
                value: retirement.runLogCoversUnreadWindow ? 'Yes' : 'No — Scheduled, not opened was not evaluated',
              },
            ]}
          />
        </Stack>
      </SectionCard>

      <SectionCard title="Certification" subtitle="Reserved by the schema">
        <Figure value={certification.isRecorded ? 'Recorded' : NOT_MEASURED} reason={certification.reason} />
      </SectionCard>

      <EvaluatedAt at={governance.evaluatedAt} />
    </Stack>
  )
}

function ResponsibilityTable({ rows }: { rows: ReportDetailResponsibilityChange[] }) {
  return (
    <Table.ScrollContainer minWidth={760}>
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>When</Table.Th>
            <Table.Th>Responsibility</Table.Th>
            <Table.Th>From</Table.Th>
            <Table.Th>To</Table.Th>
            <Table.Th>Performed by (id)</Table.Th>
            <Table.Th>Reason</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, index) => (
            <Table.Tr key={`${row.changedAt}-${index}`}>
              <Table.Td>{whenLabel(row.changedAt)}</Table.Td>
              <Table.Td>{row.responsibilityType === 'BusinessOwner' ? 'Business owner' : row.responsibilityType}</Table.Td>
              <Table.Td>{principalText(row.previousPrincipalId, row.previousPrincipalType)}</Table.Td>
              <Table.Td>{principalText(row.newPrincipalId, row.newPrincipalType)}</Table.Td>
              <Table.Td>{row.changedBy ?? NOT_MEASURED}</Table.Td>
              <Table.Td>{row.reason ?? NOT_MEASURED}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

/**
 * This report's retirement episodes: the episodes list narrowed by `reportId`,
 * plus `/by-report` for the protection actually in force (a keep, or a
 * reversal's quiet period). Gated, fetched and failed on its own.
 */
function EpisodesPanel({ reportId }: { reportId: string }) {
  const gate = useLighthouseAdmin()
  const enabled = adminPageMayFetch(gate)
  const episodes = useRetirementEpisodes({ reportId, skip: 0, top: EPISODE_PAGE_SIZE }, { enabled })
  const states = useEpisodeStates([reportId], { enabled })
  const inForce = states.data?.find((s) => s.reportId === reportId) ?? null

  return (
    <SectionCard
      title="Retirement episodes"
      subtitle="What the consent clock has done to this report — read-only; every action on it is Cartographer’s"
    >
      {episodes.isError ? (
        <TabFailure error={episodes.error} what="this report’s retirement episodes" migration={EPISODES_MIGRATION} />
      ) : episodes.isLoading || !episodes.data ? (
        <TabLoading />
      ) : (
        <EpisodesContent
          payload={episodes.data}
          inForce={
            states.isError
              ? <Text size="sm" c="dimmed">{NOT_MEASURED} — the protection in force could not be read</Text>
              : states.isLoading || !states.data
                ? <Text size="sm" c="dimmed">{NOT_MEASURED}</Text>
                : <EpisodeStateCell state={inForce} />
          }
        />
      )}
    </SectionCard>
  )
}

function EpisodesContent({ payload, inForce }: { payload: RetirementEpisodes; inForce: ReactNode }) {
  if (payload.totalCount === 0 || payload.rows.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        This report has never been on the retirement clock: no episode is recorded for it.
      </Text>
    )
  }

  return (
    <Stack gap="sm">
      <Group gap={6} align="baseline">
        <Text size="sm" fw={500}>In force now:</Text>
        {inForce}
      </Group>
      <Caption>
        {showingText(payload.rows.length, payload.totalCount)} episodes.
        {payload.summary.automaticDelistLockedBecause && <> Automatic delisting is locked: {payload.summary.automaticDelistLockedBecause}</>}
      </Caption>
      <Table.ScrollContainer minWidth={720}>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Where it stands</Table.Th>
              <Table.Th>Signals</Table.Th>
              <Table.Th>Opened</Table.Th>
              <Table.Th>Ended</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {payload.rows.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td>
                  <Stack gap={2}>
                    <Badge variant="light" color="gray" tt="none">{row.state}</Badge>
                    <Text size="xs">{episodeStatusText(row)}</Text>
                  </Stack>
                </Table.Td>
                <Table.Td>{row.signals.length ? row.signals.map(retirementSignalLabel).join(', ') : NOT_MEASURED}</Table.Td>
                <Table.Td>{whenLabel(row.createdAt)}</Table.Td>
                <Table.Td>{whenLabel(row.resolvedAt)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  )
}
